// ── Workflow Builder — Existing Customer view ─────────────────────────────────
//
// Shows the exact recipe (v1) or workflow (v2) recorded for this customer in
// DealHub's own Scope record — read-only, same "reflects DealHub's record, not
// FrankieOne's live platform" convention every other existing-customer view in
// this tool already follows (see ExistingCustomerPanel.tsx).
import { AlertCircle, ArrowRight, FileSearch, Lock } from 'lucide-react'
import { V1_RECIPES } from '@features/scope/types'
import type { BuilderWorkflow, V1RecipeDefinition, WorkflowStep, WorkflowStepType } from '@features/scope/types'
import type { ExistingCustomerSelection } from './ExistingCustomerPanel'
import type { AiConfigureSuggestions } from '../aiConfigure'
import { buildWorkflowTasksFromAcceptedStages, hasAnyAcceptedWorkflowStage } from '../workflowGraphFromSuggestions'
import type { AcceptedWorkflowStages } from '../workflowGraphFromSuggestions'
import { WorkflowGraphPreview } from './WorkflowGraphPreview'

interface StepChip {
  label: string
  sublabel?: string
  color: string
  tint: string
}

const STEP_TYPE_STYLE: Record<WorkflowStepType, { label: string; color: string; tint: string }> = {
  blocklist: { label: 'Blocklist', color: '#b91c1c', tint: 'rgba(220,38,38,0.1)' },
  gov_id: { label: 'Gov ID', color: '#1d4ed8', tint: 'rgba(37,99,235,0.1)' },
  kyc: { label: 'KYC', color: '#15803d', tint: 'rgba(22,163,74,0.1)' },
  idv_biometrics: { label: 'IDV & Biometrics', color: '#1d4ed8', tint: 'rgba(37,99,235,0.1)' },
  aml: { label: 'AML', color: '#7c3aed', tint: 'rgba(124,58,237,0.1)' },
  fraud: { label: 'Fraud', color: '#c2410c', tint: 'rgba(234,88,12,0.1)' },
  risk: { label: 'Risk', color: '#b45309', tint: 'rgba(180,83,9,0.1)' },
  death_check: { label: 'Death Check', color: '#475569', tint: 'rgba(71,85,105,0.1)' },
  duplicate: { label: 'Duplicate', color: '#4f46e5', tint: 'rgba(79,70,229,0.1)' },
}

// Generic categories in a V1 recipe's checkOrder (e.g. "Blocklist", "Identity",
// "Entity", "AML") don't map 1:1 to a WorkflowStepType — reuse the same palette
// by best-effort name match, falling back to a neutral tone.
function checkOrderStyle(name: string): { color: string; tint: string } {
  const key = name.toLowerCase()
  if (key.includes('block')) return STEP_TYPE_STYLE.blocklist
  if (key.includes('aml')) return STEP_TYPE_STYLE.aml
  if (key.includes('fraud')) return STEP_TYPE_STYLE.fraud
  if (key.includes('id validation') || key.includes('identity')) return STEP_TYPE_STYLE.gov_id
  return { color: '#475569', tint: 'rgba(71,85,105,0.1)' }
}

function StepChipStrip({ chips }: { chips: StepChip[] }) {
  return (
    <div className="flex items-center flex-wrap gap-1.5">
      {chips.map((chip, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <div className="inline-flex flex-col rounded-[8px] px-2.5 py-1.5" style={{ background: chip.tint, border: `1px solid ${chip.color}` }}>
            <span className="text-[11px] font-semibold" style={{ color: chip.color }}>{chip.label}</span>
            {chip.sublabel && <span className="text-[9px] text-[#64748b]">{chip.sublabel}</span>}
          </div>
          {i < chips.length - 1 && <ArrowRight size={12} className="text-[#94a3b8] shrink-0" />}
        </div>
      ))}
    </div>
  )
}

export function V1RecipeVisual({ recipe, workflowName, notes }: { recipe: V1RecipeDefinition; workflowName: string; notes: string }) {
  const chips: StepChip[] = recipe.checkOrder.map((co) => ({ label: co, ...checkOrderStyle(co) }))
  return (
    <div className="space-y-3">
      <div>
        <p className="text-[13px] font-semibold text-[#1e293b]">{recipe.label}</p>
        <p className="text-[11px] text-[#475569] mt-0.5">{recipe.description} · <span className="uppercase font-semibold" style={{ color: recipe.amlLevel === 'enhanced' ? '#7c3aed' : '#475569' }}>{recipe.amlLevel === 'enhanced' ? 'Enhanced AML (+ media)' : 'Standard AML'}</span></p>
      </div>
      <div>
        <p className="text-[9px] font-semibold uppercase tracking-wider text-[#64748b] mb-1.5">Check order</p>
        <StepChipStrip chips={chips} />
      </div>
      <div>
        <p className="text-[9px] font-semibold uppercase tracking-wider text-[#64748b] mb-1.5">Check types included</p>
        <div className="flex flex-wrap gap-1.5">
          {recipe.checkTypes.map((ct) => (
            <span key={ct} className="text-[10px] font-medium px-2 py-1 rounded" style={{ background: '#f8fafc', border: '1px solid #e5e7eb', color: '#475569' }}>{ct}</span>
          ))}
        </div>
      </div>
      {notes && (
        <div>
          <p className="text-[9px] font-semibold uppercase tracking-wider text-[#64748b] mb-1">Notes</p>
          <p className="text-[11px] text-[#475569]">{notes}</p>
        </div>
      )}
      <p className="text-[10px] text-[#64748b] italic">{workflowName ? `Recorded as "${workflowName}" in this customer's Scope record.` : ''}</p>
    </div>
  )
}

function V2StepsVisual({ steps, workflowName, notes }: { steps: WorkflowStep[]; workflowName: string; notes: string }) {
  const chips: StepChip[] = steps.map((s) => {
    const style = STEP_TYPE_STYLE[s.type]
    const sublabel = s.type === 'kyc' ? s.rulesetId
      : s.type === 'idv_biometrics' ? s.requirement
      : undefined
    return { label: style.label, sublabel, color: style.color, tint: style.tint }
  })
  return (
    <div className="space-y-3">
      <div>
        <p className="text-[13px] font-semibold text-[#1e293b]">{workflowName || 'Unnamed workflow'}</p>
        <p className="text-[11px] text-[#475569] mt-0.5">{steps.length} step{steps.length !== 1 ? 's' : ''}, built step-by-step in Scope&apos;s V2 workflow builder.</p>
      </div>
      <StepChipStrip chips={chips} />
      {notes && (
        <div>
          <p className="text-[9px] font-semibold uppercase tracking-wider text-[#64748b] mb-1">Notes</p>
          <p className="text-[11px] text-[#475569]">{notes}</p>
        </div>
      )}
    </div>
  )
}

interface WorkflowBuilderExistingCustomerViewProps {
  existingCustomer: ExistingCustomerSelection | null
  /** Suggestions batch + which stages the rep has accepted so far in AI
   * Configure — drives the "second account" graph below, e.g. Campaign
   * Agent's Fraud Signal Check → conditional Biometrics/IDV step-up. */
  aiSuggestions?: AiConfigureSuggestions | null
  acceptedStages?: AcceptedWorkflowStages
}

export function WorkflowBuilderExistingCustomerView({ existingCustomer, aiSuggestions, acceptedStages }: WorkflowBuilderExistingCustomerViewProps) {
  const scopeData = existingCustomer?.scopeData
  const customerName = existingCustomer?.deal.customer_name ?? 'This customer'

  const configured = scopeData?.workflowsConfig.find((w) => {
    if (w.kind === 'complex') return Boolean(w.name)
    const bw = w as BuilderWorkflow
    return Boolean(bw.v1RecipeKey) || bw.steps.length > 0 || (bw.v1CustomChecks?.length ?? 0) > 0
  })

  return (
    <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
      <div className="px-4 py-3" style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
        <p className="text-[12px] font-semibold text-[#1e293b]">Workflow — Existing Customer</p>
        <p className="text-[10px] text-[#64748b] mt-1 leading-snug">
          Shows the exact recipe or workflow recorded for this customer in DealHub&apos;s Scope record. Reads DealHub&apos;s own presales record, not FrankieOne&apos;s live platform.
        </p>
      </div>

      <div className="p-4 space-y-3">
        {!existingCustomer && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px dashed #e5e7eb' }}>
            <FileSearch size={12} className="text-[#64748b] shrink-0" />
            <p className="text-[11px] text-[#64748b]">Select a customer on Account Setup to see their configured workflow here.</p>
          </div>
        )}

        {existingCustomer && !scopeData && (
          <p className="text-[11px] text-amber-700 flex items-center gap-1.5"><AlertCircle size={12} /> No scope has been recorded for this deal yet.</p>
        )}

        {existingCustomer && scopeData && !configured && (
          <div className="px-3 py-2.5 rounded-[9px] flex items-start gap-2" style={{ background: 'rgba(251,146,60,.06)', border: '1px solid rgba(251,146,60,.25)' }}>
            <AlertCircle size={13} className="text-amber-700 shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-700">{customerName} doesn&apos;t have a workflow or recipe configured in DealHub&apos;s scope record yet.</p>
          </div>
        )}

        {existingCustomer && scopeData && configured && configured.kind === 'complex' && (
          <div className="space-y-2">
            <p className="text-[13px] font-semibold text-[#1e293b]">{configured.name}</p>
            <p className="text-[11px] text-[#475569]">Complex workflow — configured externally. Workflow ID: <span className="font-mono">{configured.workflowId || '—'}</span></p>
            {configured.notes && <p className="text-[11px] text-[#475569]">{configured.notes}</p>}
          </div>
        )}

        {existingCustomer && scopeData && configured && configured.kind !== 'complex' && (() => {
          const bw = configured as BuilderWorkflow
          const v1Recipe = bw.v1RecipeKey ? V1_RECIPES.find((r) => r.key === bw.v1RecipeKey) : undefined
          if (v1Recipe) return <V1RecipeVisual recipe={v1Recipe} workflowName={bw.name} notes={bw.notes} />
          if (bw.steps.length > 0) return <V2StepsVisual steps={bw.steps} workflowName={bw.name} notes={bw.notes} />
          return null
        })()}

        {existingCustomer && scopeData && configured && (
          <div className="flex items-start gap-2 px-3 py-2.5 rounded-[9px]" style={{ background: 'rgba(99,102,241,.06)', border: '1px solid rgba(99,102,241,.25)' }}>
            <Lock size={12} className="text-[#4f46e5] shrink-0 mt-0.5" />
            <p className="text-[11px] text-[#1e293b] leading-snug">Read-only here — edit what runs for this customer in Scope&apos;s Workflows tab, not in this tool.</p>
          </div>
        )}

        {acceptedStages && hasAnyAcceptedWorkflowStage(acceptedStages) && (
          <div className="space-y-2 pt-1">
            <div>
              <p className="text-[13px] font-semibold text-[#1e293b]">Second Account — Built from Accepted AI Configure Suggestions</p>
              <p className="text-[11px] text-[#475569] mt-0.5">
                A separate account, distinct from the safe-harbour recipe above, built from whatever&apos;s been accepted in AI Configure&apos;s conversation on Account Setup — e.g. a Fraud Signal Check on every onboarding request, stepping up to Biometrics/IDV only when it finds risk.
              </p>
            </div>
            <WorkflowGraphPreview tasks={buildWorkflowTasksFromAcceptedStages(acceptedStages, aiSuggestions)} />
          </div>
        )}
      </div>
    </div>
  )
}
